import { useMemo, useRef } from 'react';
import { storedEmailPreviewHtml } from './storedEmailPreviewHtml';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import type { StoredEmail } from '@/services/notificationSettingsService';

interface StoredEmailViewerProps {
  email: StoredEmail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Loan history is evidence-only; preserve existing admin preview behavior. */
  blockExternalContent?: boolean;
  onCloseAutoFocus?: (event: Event) => void;
  timestampLabel?: string;
  missingTimestamp?: string;
}

export function StoredEmailViewer({ email, open, onOpenChange, blockExternalContent = false, onCloseAutoFocus, timestampLabel = 'Sent', missingTimestamp = 'Unknown' }: StoredEmailViewerProps) {
  const content = useRef<HTMLDivElement>(null);
  const html = useMemo(() => blockExternalContent ? storedEmailPreviewHtml(email?.html || '') : email?.html, [blockExternalContent, email?.html]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={content} onOpenAutoFocus={blockExternalContent ? event => {
        event.preventDefault();
        content.current?.querySelector<HTMLButtonElement>('button')?.focus();
      } : undefined} onCloseAutoFocus={onCloseAutoFocus} className={blockExternalContent ? "max-h-[90vh] max-w-4xl overflow-y-auto" : "max-h-[90vh] max-w-4xl overflow-hidden"}>
        <DialogHeader>
          <DialogTitle className="truncate">{email?.subject || 'Stored email'}</DialogTitle>
        </DialogHeader>
        {email && (
          <div className="space-y-3 overflow-hidden">
            <div className={`grid gap-2 rounded-md border bg-muted/30 p-3 text-xs md:grid-cols-2 ${blockExternalContent ? 'min-w-0 break-words' : ''}`}>
              <div><span className="font-medium">{timestampLabel}:</span> {email.sentAt ? new Date(email.sentAt).toLocaleString() : missingTimestamp}</div>
              <div><span className="font-medium">Status:</span> {email.status || 'Unknown'}</div>
              <div className="md:col-span-2"><span className="font-medium">To:</span> {email.toEmails.join(', ') || 'Unknown'}</div>
              <div className="md:col-span-2"><span className="font-medium">CC:</span> {email.ccEmails.join(', ') || 'None'}</div>
              {email.context.is_test && <Badge className="w-fit">Test send</Badge>}
            </div>
            {blockExternalContent && <p className="text-xs text-muted-foreground">External links and images are disabled in this stored copy.</p>}
            <iframe
              title="Stored email body"
              sandbox=""
              srcDoc={html}
              className="h-[60vh] w-full rounded-md border bg-white"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
